import { BaseService } from './api'
import { 
  BidsQueryParams,
  OrderbookResponse,
  RfqsQueryParams,
  BidsListResponse,
  RfqsListResponse,
  OrderbookStatsResponse
} from '@/types'
import {
  Bid,
  RFQRequest,
  RFQDetails,
  PlaceBidRequest,
  PlaceBidResponse,
  PlaceRFQResponse,
  OrderDetails
} from '@/types/trading'

export class TradingService extends BaseService {
  // Orderbook Methods
  async getOrderbook(params?: BidsQueryParams): Promise<OrderbookResponse> {
    try {
      const response = await this.api.get<OrderbookResponse>('/orderbook', { params })
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch orderbook')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  async getOrderDetails(orderId: string): Promise<OrderDetails> {
    try {
      const response = await this.api.get<OrderDetails>(`/orderbook/${orderId}`)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch order details')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  // Bids Methods
  async getBids(params?: BidsQueryParams): Promise<Bid[]> {
    try {
      const response = await this.api.get<Bid[]>('/bids', { params })
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch bids')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  async placeBid(data: PlaceBidRequest): Promise<PlaceBidResponse> {
    try {
      const response = await this.api.post<PlaceBidResponse>('/bids', data)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to place bid')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  // RFQ Methods
  async placeRFQ(data: RFQRequest): Promise<PlaceRFQResponse> {
    try {
      const response = await this.api.post<PlaceRFQResponse>('/rfq', data)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to place RFQ')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  async getRFQDetails(rfqId: string): Promise<RFQDetails> {
    try {
      const response = await this.api.get<RFQDetails>(`/rfq/${rfqId}`)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch RFQ details')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  async getOrderbookStats(): Promise<OrderbookStatsResponse> {
    try {
      const response = await this.api.get<OrderbookStatsResponse>('/orderbook/stats');
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch orderbook stats');
      }
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
      console.error('Get orderbook stats failed:', error);
      }
      throw error;
    }
  }

  // User Inquiries Methods
  async getUserBids(params?: BidsQueryParams): Promise<BidsListResponse> {
    try {
      const response = await this.api.get<BidsListResponse>('/bids', { params })
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch user bids')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  async getUserRfqs(params?: RfqsQueryParams): Promise<RfqsListResponse> {
    try {
      const response = await this.api.get<RfqsListResponse>('/rfq', { params })
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch user RFQs')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }
}

export const tradingService = new TradingService(); 